import { createSelector } from 'reselect';

import { getElementId, getImageProp, getImageThumbnail } from '../../../../common/elements/utils/elementPropertyUtils';
import { hasProgressiveImageLoaded } from '../../../components/images/progressiveImages/progressiveImagesSingleton';
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';
import { getCurrentModalElementId } from '../../modal/elementModalCommonSelector';
import { getElements } from '../../selectors/elementsSelector';
import { createShallowSelector } from '../../../utils/milanoteReselect/milanoteReselect';
import { getImageSource } from '../imageHelper';
import { isImage } from '../../../../common/elements/utils/elementTypeUtils';
import { IMAGE_SIZES } from '../../../../common/images/imageConstants';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { ReduxState } from '../../../types/reduxTypes';

const useSecureMediaUrl = true;

type ImageModalSelectorProps = {
    element: ImMNElement;
};

const renderSecureImagesThroughCFSelector = getIsFeatureEnabledForCurrentUser(
    ExperimentId.renderSecureImagesThroughCF,
    true,
);

/**
 * Uses the best already-loaded progressive image as the modal placeholder before the full image resolves.
 */
export const lowerQualityImageUrlSelector = createSelector(
    (_: ReduxState, { element }: ImageModalSelectorProps) => element,
    renderSecureImagesThroughCFSelector,
    (element, renderSecureImagesThroughCF) => {
        const imageDetails = getImageProp(element);
        const elementId = getElementId(element);
        const imageUrls = [IMAGE_SIZES.LARGE, IMAGE_SIZES.REGULAR].map((imageSize) =>
            getImageSource({
                imageDetails,
                imageSize: imageSize.name,
                elementId,
                useSecureMediaUrl,
                renderSecureImagesThroughCF,
            }),
        );

        return imageUrls.find((url) => hasProgressiveImageLoaded(url)) || getImageThumbnail(element);
    },
);

const imageModalV2EnabledSelector = getIsFeatureEnabledForCurrentUser(ExperimentId.imageModalV2);
const EMPTY_ELEMENT_IDS: readonly string[] = [];

/**
 * Selects only the current modal element so unrelated element updates do not invalidate downstream selectors.
 */
const currentModalElementSelector = createSelector(getCurrentModalElementId, getElements, (modalElementId, elements) =>
    modalElementId ? elements.get(modalElementId) : undefined,
);

/**
 * Gets the elements whose transfers are already shown by the open V2 image modal.
 */
export const imageModalToastElementIdsSelector = createShallowSelector(
    imageModalV2EnabledSelector,
    getCurrentModalElementId,
    currentModalElementSelector,
    (isV2ModalEnabled, modalElementId, modalElement) => {
        if (!isV2ModalEnabled || !modalElementId) return EMPTY_ELEMENT_IDS;

        return isImage(modalElement) ? [modalElementId] : EMPTY_ELEMENT_IDS;
    },
);
